import type {
  OocyteCohort,
  Specimen,
} from "@src/packages/care/cryovault/useSpecimens";
import { getRemainingOocyteCohortCount } from "@src/packages/care/cryovault/oocyteCohortInventory";
import { CONSUMING_SPECIMEN_USAGES } from "@src/packages/care/cryovault/frozenSpecimenUsages";
import { isInFrozenStorage } from "@src/packages/care/cryovault/frozenSpecimenAvailability";
import type { Cycle, Phase } from "../hooks/useCycles";
import {
  resolvePostProcedureOutcomeClinicalState,
  type PostProcedureOutcomeClinicalState,
} from "./postProcedureOutcomeState";

const OBSERVED_RESULT_ORDER = [
  "retrieved_oocytes",
  "m2_count",
  "fertilized_oocytes",
  "embryos_day_3",
  "embryos_day_5",
  "frozen_embryo",
  "transferred_embryo",
] as const;

export type ObservedResultMetric = {
  metric: (typeof OBSERVED_RESULT_ORDER)[number] | string;
  value: number;
  phaseId: string;
  metricId: string | null;
};

type ApiFertilityMetric = NonNullable<Phase["fertilityMetrics"]>[number];
type ObservedResultsPhase = Pick<Phase, "id"> & {
  fertilityMetrics?:
    | readonly Partial<Pick<ApiFertilityMetric, "id" | "type" | "value">>[]
    | null;
};

/**
 * Read the metrics that the API actually recorded for a cycle.
 *
 * A missing metric is deliberately omitted; it is not equivalent to zero.
 * The phase and metric IDs stay attached so callers can explain where a
 * displayed value came from and future corrections can target the source row.
 */
export function extractObservedResults(
  phases: readonly ObservedResultsPhase[],
): ObservedResultMetric[] {
  const metrics = phases.flatMap((phase) =>
    (phase.fertilityMetrics ?? []).map((metric) => ({ phase, metric })),
  );

  return OBSERVED_RESULT_ORDER.flatMap((metricType) => {
    const match = metrics.find(
      ({ metric }) => metric.type === metricType && metric.value != null,
    );
    if (!match || match.metric.value == null) return [];

    return [
      {
        metric: metricType,
        value: match.metric.value,
        phaseId: match.phase.id,
        metricId: match.metric.id ?? null,
      },
    ];
  });
}

export type PostCycleLifecycleState =
  | "in_progress"
  | "results_recorded_open"
  | "completed_preservation"
  | "completed_with_outcome"
  | "closed_unspecified"
  | "cancelled";

export type PostCycleLifecycle = {
  state: PostCycleLifecycleState;
  status: Cycle["status"];
  cycleType: Cycle["cycleType"];
  clinicalState: PostProcedureOutcomeClinicalState;
  outcomeType: NonNullable<Cycle["outcome"]>["type"] | null;
  resultStatus: NonNullable<Cycle["outcome"]>["resultStatus"] | null;
  verificationStatus:
    NonNullable<Cycle["outcome"]>["verificationStatus"] | null;
  acknowledgedAt: string | null;
};

export const PRESERVATION_CYCLE_TYPES = new Set<Cycle["cycleType"]>([
  "egg_freezing",
  "embryo_freezing",
  "sperm_freezing",
]);

// Egg and embryo freezing expose a source-backed final quantity through the
// frozen_embryo metric (the API uses that metric for eggs frozen as well).
// Do not turn a closed cycle into a completed preservation cycle until that
// final result exists; retrieved or mature counts are not the preservation
// outcome.
const PRESERVATION_CYCLE_TYPES_REQUIRING_FINAL_RESULT = new Set<
  Cycle["cycleType"]
>(["egg_freezing", "embryo_freezing"]);

/**
 * Resolve lifecycle without making a clinical decision from inventory.
 * Outcome meaning is delegated to the canonical post-procedure resolver;
 * acknowledgement and verification remain visible metadata.
 */
export function resolvePostCycleLifecycle(
  cycle: Pick<Cycle, "status" | "cycleType"> & {
    outcome: Pick<
      NonNullable<Cycle["outcome"]>,
      "type" | "resultStatus" | "verificationStatus" | "acknowledgedAt"
    > | null;
  },
  observedResultCount = 0,
  finalPreservationResultRecorded = false,
): PostCycleLifecycle {
  const outcome = cycle.outcome;
  const clinicalState = resolvePostProcedureOutcomeClinicalState({
    type: outcome?.type,
    resultStatus: outcome?.resultStatus,
    verificationStatus: outcome?.verificationStatus,
  });

  let state: PostCycleLifecycleState;
  if (clinicalState === "cancelled") {
    state = "cancelled";
  } else if (cycle.status === "open") {
    state = observedResultCount > 0 ? "results_recorded_open" : "in_progress";
  } else if (
    clinicalState === "pregnancy" ||
    clinicalState === "no_pregnancy" ||
    clinicalState === "adverse"
  ) {
    state = "completed_with_outcome";
  } else if (
    PRESERVATION_CYCLE_TYPES.has(cycle.cycleType) &&
    (!PRESERVATION_CYCLE_TYPES_REQUIRING_FINAL_RESULT.has(cycle.cycleType) ||
      finalPreservationResultRecorded)
  ) {
    state = "completed_preservation";
  } else {
    state = "closed_unspecified";
  }

  return {
    state,
    status: cycle.status,
    cycleType: cycle.cycleType,
    clinicalState,
    outcomeType: outcome?.type ?? null,
    resultStatus: outcome?.resultStatus ?? null,
    verificationStatus: outcome?.verificationStatus ?? null,
    acknowledgedAt: outcome?.acknowledgedAt ?? null,
  };
}

type InventorySpecimen = Pick<
  Specimen,
  | "id"
  | "type"
  | "status"
  | "cycleId"
  | "verificationStatus"
  | "storageLocations"
  | "specimenUsages"
>;
type InventoryCohort = Pick<
  OocyteCohort,
  | "id"
  | "originCycleId"
  | "status"
  | "numberFrozen"
  | "numberThawed"
  | "verificationStatus"
  | "members"
>;

export type PostCycleInventoryScope =
  | { kind: "vault" }
  | { kind: "origin_cycle"; cycleId: string }
  | { kind: "consuming_cycle"; cycleId: string };

export type PostCycleInventorySnapshot = {
  counts: Record<Specimen["type"], number>;
  specimenIds: string[];
  cohortIds: string[];
  /**
   * How many of the counted units she recorded herself.
   *
   * Not a reason to leave them out — it used to be, and the homepage's vault
   * card was empty for every patient because of it. It is what lets a surface
   * say what its figures rest on instead of pretending they are confirmed, or
   * pretending they do not exist.
   */
  selfRecordedCount: number;
  unknownLinkage: boolean;
};

export type PostCycleSectionState =
  "loading" | "error" | "partial" | "empty" | "unknown_linkage" | "ready";

export function resolvePostCycleSectionState({
  isLoading,
  isError,
  hasData,
  hasUnknownLinkage = false,
}: {
  isLoading: boolean;
  isError: boolean;
  hasData: boolean;
  hasUnknownLinkage?: boolean;
}): PostCycleSectionState {
  if (isLoading && hasData) return "partial";
  if (isError && hasData) return "partial";
  if (isError) return "error";
  if (isLoading) return "loading";
  if (hasUnknownLinkage) return "unknown_linkage";
  if (!hasData) return "empty";
  return "ready";
}

const EMPTY_COUNTS: Record<Specimen["type"], number> = {
  semen: 0,
  oocyte: 0,
  embryo: 0,
};

function matchesSpecimenScope(
  specimen: InventorySpecimen,
  scope: PostCycleInventoryScope,
): boolean {
  if (scope.kind === "vault") return true;
  if (scope.kind === "origin_cycle") return specimen.cycleId === scope.cycleId;

  return specimen.specimenUsages.some(
    (usage) =>
      usage.cycleId === scope.cycleId &&
      CONSUMING_SPECIMEN_USAGES.has(usage.usage),
  );
}

function matchesCohortScope(
  cohort: InventoryCohort,
  scope: PostCycleInventoryScope,
): boolean {
  if (scope.kind === "vault") return true;
  if (scope.kind === "origin_cycle") {
    return cohort.originCycleId === scope.cycleId;
  }
  return cohort.members.some((member) => member.cycleId === scope.cycleId);
}

/**
 * Build a reconciled inventory snapshot. Cohort totals are authoritative for
 * oocytes; member rows are linkage evidence and are never counted twice.
 */
export function buildPostCycleInventorySnapshot(
  specimens: readonly InventorySpecimen[] | undefined,
  cohorts: readonly InventoryCohort[] | undefined,
  scope: PostCycleInventoryScope,
): PostCycleInventorySnapshot {
  const counts = { ...EMPTY_COUNTS };
  const specimenIds: string[] = [];
  const cohortIds: string[] = [];
  const cohortMemberIds = new Set(
    (cohorts ?? []).flatMap((cohort) =>
      cohort.members.map((member) => member.id),
    ),
  );
  let selfRecordedCount = 0;
  let unknownLinkage = false;
  /*
   * The cycles a counted cohort already speaks for.
   *
   * "Cohort totals are authoritative for oocytes" only held while member rows
   * were the sole way to spot a duplicate. A cohort can carry `numberFrozen`
   * with an empty `members` array — `createOocyteCohort` inserts members only
   * when it is given some, and this client never creates cohorts at all — so a
   * provider-ingested cohort of fifty-four beside the fifty-four rows she
   * recorded herself would have counted a hundred and eight. Its origin cycle
   * is the linkage that survives when the member rows do not.
   *
   * Only for cohorts carrying none: where members exist they already say which
   * rows are covered, and widening the exclusion to the whole cycle would drop
   * oocytes the cohort's own total never claimed.
   */
  const cohortCoveredCycleIds = new Set<string>();

  for (const cohort of cohorts ?? []) {
    if (!matchesCohortScope(cohort, scope)) continue;
    if (cohort.status !== "stored" && cohort.status !== "partially_used") {
      continue;
    }
    // A clinic has contradicted this record. See the specimen loop below.
    if (cohort.verificationStatus === "refuted") continue;

    const remaining = getRemainingOocyteCohortCount(
      cohort.numberFrozen,
      cohort.numberThawed,
      cohort.members.length,
    );
    if (remaining > 0) {
      counts.oocyte += remaining;
      cohortIds.push(cohort.id);
      if (cohort.originCycleId && cohort.members.length === 0) {
        cohortCoveredCycleIds.add(cohort.originCycleId);
      }
      if (cohort.verificationStatus === "unverified") {
        selfRecordedCount += remaining;
      }
    }
  }

  for (const specimen of specimens ?? []) {
    if (!matchesSpecimenScope(specimen, scope)) continue;
    if (!isInFrozenStorage(specimen)) continue;
    /*
     * Refuted is not "not yet confirmed" — it is a clinic saying this record is
     * wrong. Counting it would put it in her total and, worse, under a caveat
     * offering that a clinic report could confirm it later. One already
     * disagreed.
     *
     * Excluded here rather than in `isInFrozenStorage`, because that rule
     * answers whether material is physically in storage and backs the CryoVault
     * screen too. What a projection may be built on is a different question,
     * and it belongs to the projection.
     */
    if (specimen.verificationStatus === "refuted") continue;
    if (cohortMemberIds.has(specimen.id)) continue;
    if (
      specimen.type === "oocyte" &&
      specimen.cycleId != null &&
      cohortCoveredCycleIds.has(specimen.cycleId)
    ) {
      continue;
    }

    counts[specimen.type] += 1;
    specimenIds.push(specimen.id);
    if (specimen.verificationStatus === "unverified") selfRecordedCount += 1;
  }

  if (scope.kind === "consuming_cycle") {
    const hasAvailableCohort = (cohorts ?? []).some(
      (cohort) =>
        cohort.status === "stored" || cohort.status === "partially_used",
    );
    const hasLinkedInventory = specimenIds.length > 0;
    unknownLinkage = hasAvailableCohort && !hasLinkedInventory;
  }

  return {
    counts,
    specimenIds,
    cohortIds,
    selfRecordedCount,
    unknownLinkage,
  };
}
